import { useRef } from "react";

/** Input nominal rupiah berformat titik ribuan.
 *
 * Dibuat sebagai komponen BERSAMA (bukan disalin per halaman) karena ada ±60
 * input nominal di aplikasi ini dan sebelumnya tiap halaman menanganinya
 * sendiri-sendiri — sebagian tanpa format sama sekali, sebagian dengan bug yang
 * sama berulang kali.
 *
 * Dua masalah nyata yang ditutup di sini:
 *
 * 1. TITIK RIBUAN. "18500000" mustahil dibaca cepat; salah satu nol lebih/kurang
 *    tidak akan ketahuan sampai uangnya salah. Tampil "18.500.000" membuat
 *    kesalahan itu langsung kelihatan.
 *
 * 2. ANGKA NOL YANG NYANGKUT DI DEPAN. Banyak form memberi nilai awal 0 dan
 *    menampilkannya apa adanya, jadi mengetik "200000" menghasilkan "0200000"
 *    dan nilainya salah 10x lipat. Di sini nilai 0 ditampilkan sebagai KOSONG
 *    (angka 0 pindah ke placeholder), sehingga ketikan pertama selalu memulai
 *    angka baru — persis yang diminta.
 *
 * Kontrak: `value` berupa angka (atau "" untuk kosong), `onChange` menerima
 * ANGKA — pemanggil tidak perlu mem-parse ulang.
 */

export function formatRibuan(v) {
  const s = String(v ?? "").replace(/\D/g, "");
  if (!s) return "";
  return Number(s).toLocaleString("id-ID");
}

/** "Rp 1.250.000" / "1.250.000" / "1250000" → 1250000 */
export function parseRupiah(teks) {
  const s = String(teks ?? "").replace(/\D/g, "");
  return s ? Number(s) : 0;
}

export default function InputRupiah({
  value,
  onChange,
  className = "",
  placeholder = "0",
  testid,
  disabled = false,
  // Untuk field yang KOSONG dan NOL punya arti berbeda — mis. "denda per jam"
  // pada data karyawan: kosong = ikut setelan global perusahaan, sedangkan 0 =
  // karyawan ini memang tidak didenda. Tanpa saklar ini keduanya tertukar dan
  // pengecualian per orang tidak bisa dibuat sama sekali.
  // Saat aktif: 0 tampil sebagai "0", dan mengosongkan input mengirim `null`.
  bolehKosong = false,
  ...rest
}) {
  const ref = useRef(null);

  const kosong = value === "" || value === null || value === undefined;
  // Nilai 0 tampil kosong (lihat alasan (2) di atas) KECUALI saat nol memang
  // punya makna tersendiri — lihat `bolehKosong`.
  const tampil = kosong || (!bolehKosong && Number(value) === 0)
    ? ""
    : formatRibuan(value);

  const handleChange = (e) => {
    const el = e.target;
    const sebelum = el.value;
    const posisi = el.selectionStart ?? sebelum.length;
    // Berapa DIGIT yang ada di kiri kursor — titik pemisah diabaikan supaya
    // kursor tidak melompat ke ujung setiap kali format dipasang ulang.
    const digitKiri = sebelum.slice(0, posisi).replace(/\D/g, "").length;

    const adaDigit = /\d/.test(sebelum);
    // Input dikosongkan total: kirim null hanya kalau pemanggil memang
    // membedakan kosong dari nol — selain itu 0 seperti biasa.
    onChange?.(bolehKosong && !adaDigit ? null : parseRupiah(sebelum));

    // Kembalikan kursor ke tempat yang sama secara logis (setelah digit ke-N).
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      const teksBaru = node.value;
      let hitung = 0;
      let target = teksBaru.length;
      for (let i = 0; i < teksBaru.length; i++) {
        if (/\d/.test(teksBaru[i])) hitung++;
        if (hitung === digitKiri) { target = i + 1; break; }
      }
      if (digitKiri === 0) target = 0;
      try { node.setSelectionRange(target, target); } catch { /* input tipe lain */ }
    });
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      value={tampil}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testid}
      className={className}
      {...rest}
    />
  );
}
